import { useEffect, useId, useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ProgressBar, Stepper, cx, type StepperItem } from '../ui';
import { FirmHeader, FirmPage } from '../firm/FirmHeader';
import { useIntake } from './IntakeContext';
import { BOOKING_STEP, DONE_STEP, progress, REVIEW_STEP, STEP_TITLES } from './logic';

const STEPPER_STEPS = Array.from({ length: BOOKING_STEP }, (_, i) => i + 1);

/** Header, step card and the main column. `step` 0–8 (6 = review, 7 = booking, 8 = confirmation). */
export function IntakeLayout({ step, maxReached, onGo, children }: { step: number; maxReached: number; onGo?: (n: number) => void; children: ReactNode }) {
  const { state, answers, issues, saveLabel, saveStatus } = useIntake();
  const [stepsOpen, setStepsOpen] = useState(false);
  const listId = useId();
  useEffect(() => { setStepsOpen(false); }, [step]);
  const p = progress(step);
  // A visited step with an unanswered question is "partly done", never a check. Continue is never blocked by it.
  const incomplete = (n: number) => issues.some((i) => i.step === n && (i.severity === 'error' || i.code === 'unanswered'));
  const items: StepperItem[] = STEPPER_STEPS.map((n) => {
    const done = n < step || (n < maxReached && n !== step && n <= REVIEW_STEP) || (n === BOOKING_STEP && step >= DONE_STEP);
    return {
      num: n,
      label: STEP_TITLES[n]!,
      done: done && !incomplete(n),
      partial: done && incomplete(n),
      current: n === step,
      onGo: onGo && n <= REVIEW_STEP && step <= REVIEW_STEP && n <= maxReached ? () => onGo(n) : undefined,
    };
  });
  const compactLabel = step === 0 ? 'Welcome · About 10 minutes'
    : step <= REVIEW_STEP ? `Step ${step} of ${REVIEW_STEP} · ${STEP_TITLES[step]}` : step === BOOKING_STEP ? STEP_TITLES[BOOKING_STEP] : 'Complete';
  // Nothing to report until the firm's first save (this visit or an earlier one).
  const showSave = step <= REVIEW_STEP && (state.savedAt !== null || saveStatus !== 'idle');
  return (
    <FirmPage>
      <FirmHeader
        subtitle={answers.firmName || state.firmName}
        lead={state.lead}
        saveLabel={showSave ? saveLabel : undefined}
        saveState={saveStatus === 'saving' ? 'saving' : saveStatus === 'error' ? 'error' : 'ok'}
      />
      <div className="mx-auto flex max-w-shell flex-wrap items-start gap-7 px-4 pb-16 pt-6 sm:px-8 sm:pt-8">
        {/* Below md: a one-line "Step N of 6" header with the full list behind "All steps". */}
        <nav aria-label="Onboarding steps" className="box-border flex w-full flex-col gap-[18px] rounded-2xl border border-line bg-white px-4 py-3.5 md:w-auto md:max-w-[300px] md:flex-[1_1_260px] md:p-[22px]">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 md:hidden">
              <span className="min-w-0 text-sm font-semibold text-ink">{compactLabel}</span>
              <button
                type="button"
                aria-expanded={stepsOpen}
                aria-controls={listId}
                onClick={() => setStepsOpen((o) => !o)}
                className="-my-2 -mr-2 inline-flex min-h-11 flex-none items-center gap-1 rounded-lg border-0 bg-transparent px-2 text-[13px] font-semibold text-brand hover:bg-brand-tint"
              >
                {stepsOpen ? 'Hide steps' : 'All steps'}
                {stepsOpen ? <ChevronUp aria-hidden="true" size={16} /> : <ChevronDown aria-hidden="true" size={16} />}
              </button>
            </div>
            <span className="hidden text-[13px] font-semibold text-muted md:inline">About 10 minutes</span>
            <div role="progressbar" aria-label="Progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={p.pct} aria-valuetext={p.label}>
              <ProgressBar pct={p.pct} />
            </div>
          </div>
          <div id={listId} className={cx(stepsOpen ? 'block' : 'hidden', 'md:block')}>
            <Stepper items={items} />
          </div>
          <div className={cx(stepsOpen ? 'flex' : 'hidden', 'flex-col gap-1.5 border-t border-line-soft pt-4 md:flex')}>
            <span className="text-xs font-bold uppercase tracking-[0.06em] text-muted">You are in: Plan</span>
            <span className="text-[13px] leading-[1.5] text-ink-2">Nothing changes in your current systems until you approve your migration plan.</span>
          </div>
        </nav>
        <main className="relative flex min-w-0 flex-[999_1_560px] flex-col gap-5">
          {children}
        </main>
      </div>
    </FirmPage>
  );
}
